/* eslint-disable */
import React, { createContext, useContext, useRef, useState, useCallback } from "react";

/**
 * Exclusivité audio :
 * - currentPlayer: 'navigation' (mini) | 'external' (Spotify/YouTube) | null
 * - startMiniExclusivity(ms): démarre un verrou court qui
 *     (a) ignore les setActivePlayer('external')
 *     (b) tente de PAUSE Spotify/YT en boucle toutes les 200ms
 * - isMiniExclusivityActive(): vrai si le verrou est actif
 */
const AudioCtx = createContext(null);

export function AudioProvider({ children }) {
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const miniPlayerAudioRef = useRef(null);

  // Contrôleurs Spotify (alimentés par la page Musique)
  const spotifyControllersRef = useRef(new Map()); // id -> EmbedController

  // Verrou d’exclusivité
  const exclusivityUntilRef = useRef(0);
  const exclusivityIntervalRef = useRef(null);

  const pauseMini = useCallback(() => {
    const a = miniPlayerAudioRef.current;
    if (a && !a.paused) {
      try { a.pause(); } catch {}
    }
  }, []);

  const pauseYouTubeIframes = useCallback(() => {
    try {
      const iframes = document.querySelectorAll('iframe[src*="youtube.com/embed"]');
      iframes.forEach((f) => {
        try {
          f.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
            "*"
          );
        } catch {}
      });
    } catch {}
  }, []);

  const pauseSpotifyEmbeds = useCallback(() => {
    try {
      spotifyControllersRef.current.forEach((ctrl) => {
        try { ctrl?.pause?.(); } catch {}
      });
    } catch {}
  }, []);

  // Stop explicite des lecteurs externes, utilisé par l'interface Musique.
  // N'altère pas la logique d'exclusivité existante : il expose simplement
  // les mêmes commandes de pause déjà utilisées par le mini-lecteur.
  const stopExternalPlayers = useCallback(() => {
    pauseYouTubeIframes();
    pauseSpotifyEmbeds();
    try {
      document.querySelectorAll('video').forEach((video) => {
        try { video.pause(); } catch {}
      });
    } catch {}
    setCurrentPlayer(null);
  }, [pauseYouTubeIframes, pauseSpotifyEmbeds]);

  // Démarre un verrou et pause en boucle les lecteurs externes pendant ce verrou
  const startMiniExclusivity = useCallback((ms = 1800) => {
    exclusivityUntilRef.current = Date.now() + ms;

    // Pause immédiate des externes
    pauseYouTubeIframes();
    pauseSpotifyEmbeds();

    // Relance la pause toutes les 200ms tant que le verrou est actif
    try { if (exclusivityIntervalRef.current) clearInterval(exclusivityIntervalRef.current); } catch {}
    exclusivityIntervalRef.current = setInterval(() => {
      if (Date.now() < exclusivityUntilRef.current) {
        pauseYouTubeIframes();
        pauseSpotifyEmbeds();
      } else {
        clearInterval(exclusivityIntervalRef.current);
        exclusivityIntervalRef.current = null;
      }
    }, 200);
  }, [pauseYouTubeIframes, pauseSpotifyEmbeds]);

  const isMiniExclusivityActive = useCallback(() => {
    return Date.now() < exclusivityUntilRef.current;
  }, []);

  const setActivePlayer = useCallback(
    (who /* 'navigation' | 'external' */) => {
      // Ignorer les tentatives 'external' pendant le verrou
      if (who === "external" && isMiniExclusivityActive()) {
        return;
      }

      if (who === currentPlayer) return;

      if (who === "external") {
        // Un embed joue -> on coupe le mini
        pauseMini();
      } else if (who === "navigation") {
        // Le mini repart -> on coupe YT/Spotify
        pauseYouTubeIframes();
        pauseSpotifyEmbeds();
      }
      setCurrentPlayer(who);
    },
    [currentPlayer, pauseMini, pauseYouTubeIframes, pauseSpotifyEmbeds, isMiniExclusivityActive]
  );

  // API pour la page Musique (enregistrer les contrôleurs Spotify)
  const registerSpotifyController = useCallback((id, controller) => {
    try { spotifyControllersRef.current.set(id, controller); } catch {}
  }, []);
  const unregisterSpotifyController = useCallback((id) => {
    try { spotifyControllersRef.current.delete(id); } catch {}
  }, []);

  return (
    <AudioCtx.Provider
      value={{
        currentPlayer,
        setActivePlayer,
        startMiniExclusivity,
        isMiniExclusivityActive,
        registerSpotifyController,
        unregisterSpotifyController,
        miniPlayerAudioRef,
        pauseMini,
        stopExternalPlayers,
      }}
    >
      {children}
    </AudioCtx.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within <AudioProvider>");
  return ctx;
}
